import {
  DragCancelEvent,
  DragOverEvent,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  reorderInteractions,
  reorderTakes,
  selectTakes3D,
  setSelectedInteraction,
  setSelectedTake,
  updateSelectedTake,
} from "../../app/slices/TrainingSlice";
import { useDispatch } from "react-redux";
import Interaction from "../../models/interactions/Interaction";
import Take3DCard from "./Take3DCard";
import InteractionCard from "../training_timeline/InteractionCard";

export default function useTimeline3DTakesList() {
  const takes = useSelector(selectTakes3D);
  const dispatch = useDispatch();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggingTake, setDraggingTake] = useState<boolean | null>(null);
  const [hoverTakeId, setHoverTakeId] = useState<string | null>(null);
  const activeInteractionTakeIdRef = useRef<string | null>(null);
  const pendingDestinationTakeIdRef = useRef<string | null>(null);
  const pendingOverIdRef = useRef<string | null>(null);

  const findTakeByInteractionId = (interactionId: string) =>
    takes.find((take) =>
      take.interactions.some(
        (interaction: Interaction) => interaction.id === interactionId
      )
    );

  const resolveDestinationTakeId = (over: DragOverEvent["over"]) => {
    if (!over) return null;
    const containerId = over.data?.current?.sortable.containerId?.toString();
    if (!containerId) {
      const overId = over.id.toString();
      if (takes.some((take) => take.id === overId)) return overId;
      return findTakeByInteractionId(overId)?.id ?? null;
    }
    if (containerId === "takes3d") return over.id.toString();
    return containerId;
  };

  const resetDragState = () => {
    setActiveId(null);
    setDraggingTake(null);
    setHoverTakeId(null);
    activeInteractionTakeIdRef.current = null;
    pendingDestinationTakeIdRef.current = null;
    pendingOverIdRef.current = null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const dragId = event.active.id.toString();
    setActiveId(dragId);
    pendingDestinationTakeIdRef.current = null;
    pendingOverIdRef.current = null;

    if (takes.some((take) => take.id === event.active.id)) {
      // select the take we are dragging to selected
      dispatch(setSelectedTake(event.active.id.toString()));
      setDraggingTake(true);
      setHoverTakeId(null);
      activeInteractionTakeIdRef.current = null;
    } else {
      // set the interaction we are dragging to selected
      dispatch(setSelectedInteraction(event.active.id.toString()));
      setDraggingTake(false);
      const activeData = event.active.data;
      const sourceTakeId =
        activeData?.current?.sortable.containerId?.toString() ??
        findTakeByInteractionId(dragId)?.id ??
        null;
      setHoverTakeId(null);
      activeInteractionTakeIdRef.current = sourceTakeId;
    }
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    resetDragState();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      resetDragState();
      return;
    }

    // if we are dragging takes, re-order the takes
    if (draggingTake) {
      // calculate indices
      const oldIndex = takes.findIndex((take) => take.id === active.id);
      const newIndex = takes.findIndex((take) => take.id === over.id);

      // dispatch to slice
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        dispatch(reorderTakes({ startIndex: oldIndex, endIndex: newIndex }));
      }
    }
    // if we are dragging interactions, re-order them
    else {
      const activeInteractionId = active.id.toString();
      const activeData = event.active.data;
      const trackedTakeId =
        activeInteractionTakeIdRef.current ??
        activeData?.current?.sortable.containerId?.toString() ??
        null;
      const take =
        findTakeByInteractionId(activeInteractionId) ??
        (trackedTakeId && takes.find((item) => item.id === trackedTakeId));
      const destinationTakeId = resolveDestinationTakeId(over);
      const effectiveDestinationTakeId =
        destinationTakeId ?? pendingDestinationTakeIdRef.current;
      const destinationTake =
        effectiveDestinationTakeId &&
        takes.find((item) => item.id === effectiveDestinationTakeId);
      if (!take || !destinationTake) {
        resetDragState();
        return;
      }

      // same-take drop: reorder in place
      if (take.id === destinationTake.id) {
        const oldIndex = take.interactions.findIndex(
          (i: Interaction) => i.id === activeInteractionId
        );
        const newIndex = take.interactions.findIndex(
          (i: Interaction) => i.id === over.id.toString()
        );

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          dispatch(
            reorderInteractions({
              startTakeId: take.id,
              destinationTakeId: take.id,
              startIndex: oldIndex,
              endIndex: newIndex,
            })
          );
        }
      } else {
        // cross-take drop: move once on drag end to avoid drag-over update loops
        const startIndex = take.interactions.findIndex(
          (i: Interaction) => i.id === activeInteractionId
        );
        if (startIndex === -1) {
          resetDragState();
          return;
        }
        const overId = pendingOverIdRef.current ?? over.id.toString();
        const destinationIndex = destinationTake.interactions.findIndex(
          (i: Interaction) => i.id === overId
        );
        const endIndex = destinationIndex === -1 ? 0 : destinationIndex;

        if (!destinationTake.isExpanded) {
          dispatch(updateSelectedTake({ ...destinationTake, isExpanded: true }));
        }
        dispatch(
          reorderInteractions({
            startTakeId: take.id,
            destinationTakeId: destinationTake.id,
            startIndex,
            endIndex,
          })
        );
        dispatch(setSelectedTake(destinationTake.id));
      }
    }

    resetDragState();
  };

  // used to handle dragging from one take to another
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    // we no active id or we are dragging a take, return
    // we only need this function for interactions
    if (draggingTake) return;
    if (!activeId || activeId !== active.id.toString()) return;
    if (!over) return;

    const activeInteractionId = active.id.toString();
    const destinationTakeId = resolveDestinationTakeId(over);
    if (!destinationTakeId) return;
    setHoverTakeId((current) =>
      current === destinationTakeId ? current : destinationTakeId
    );
    pendingDestinationTakeIdRef.current = destinationTakeId;
    pendingOverIdRef.current = over.id.toString();
    const sourceTakeIdFromData =
      activeInteractionTakeIdRef.current ??
      active.data?.current?.sortable.containerId?.toString() ??
      null;
    activeInteractionTakeIdRef.current =
      findTakeByInteractionId(activeInteractionId)?.id ?? sourceTakeIdFromData;
  };

  const getDragOverlay = () => {
    if (!activeId) return <></>;

    if (draggingTake) {
      const take = takes.find((take) => take.id === activeId);
      if (!take) return <></>;
      return <Take3DCard take={take} index={-1} isSelected={false} ghost />;
    } else {
      const interaction = takes
        .flatMap((take) => take.interactions)
        .find((item) => item.id === activeId);
      if (!interaction) return <></>;
      return <InteractionCard interaction={interaction} ghost />;
    }
  };
  return {
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    handleDragOver,
    hoverTakeId,
    getDragOverlay,
  } as const;
}
